import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { contentAPI } from '../services/api';
import AdminBackLink from '../components/AdminBackLink.jsx';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import './ModuleAdmin.css';

const HomeCategoryOrder = () => {
  const queryClient = useQueryClient();
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [scopeSubcategoryId, setScopeSubcategoryId] = useState('');
  const [orderedLinks, setOrderedLinks] = useState([]);
  const [draggingId, setDraggingId] = useState(null);

  const categoriesQuery = useQuery(['home-categories-admin'], async () => {
    const res = await contentAPI.getHomeCategories({ includeInactive: 'true' });
    return res.data?.data || [];
  });

  const categories = categoriesQuery.data || [];

  useEffect(() => {
    if (!selectedCategoryId && categories.length > 0) {
      setSelectedCategoryId(String(categories[0].id));
    }
  }, [categories, selectedCategoryId]);

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(selectedCategoryId)) || null,
    [categories, selectedCategoryId]
  );

  const scopeOptions = useMemo(() => {
    if (!selectedCategory) return [];
    const subcats = Array.isArray(selectedCategory.subcategories) ? selectedCategory.subcategories : [];
    return [
      { value: '', label: 'Main category' },
      ...subcats.map((sub) => ({ value: String(sub.id), label: `Subcategory: ${sub.name}` }))
    ];
  }, [selectedCategory]);

  const linksQuery = useQuery(
    ['home-category-treks-order', selectedCategoryId, scopeSubcategoryId],
    async () => {
      const params = scopeSubcategoryId ? { subcategoryId: scopeSubcategoryId } : undefined;
      const res = await contentAPI.getHomeCategoryTreks(selectedCategoryId, params);
      return res.data?.data || [];
    },
    { enabled: !!selectedCategoryId }
  );

  useEffect(() => {
    const rows = linksQuery.data || [];
    const sorted = [...rows].sort((a, b) => {
      const ao = a.order ?? 0;
      const bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      const ad = new Date(a.createdAt || 0).getTime();
      const bd = new Date(b.createdAt || 0).getTime();
      return ad - bd;
    });
    setOrderedLinks(sorted);
  }, [linksQuery.data]);

  const saveOrderMutation = useMutation(
    ({ categoryId, payload }) => contentAPI.setHomeCategoryTreks(categoryId, payload),
    {
      onSuccess: () => {
        if (selectedCategoryId) {
          queryClient.invalidateQueries(['home-category-treks-order', selectedCategoryId, scopeSubcategoryId]);
          queryClient.invalidateQueries(['home-category-treks', selectedCategoryId, scopeSubcategoryId]);
        }
      }
    }
  );

  const onDragStart = (id) => {
    setDraggingId(id);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDrop = (targetId) => {
    if (!draggingId || draggingId === targetId) return;
    const current = [...orderedLinks];
    const fromIndex = current.findIndex((item) => String(item.id) === String(draggingId));
    const toIndex = current.findIndex((item) => String(item.id) === String(targetId));
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);
    setOrderedLinks(current);
    setDraggingId(null);
  };

  const handleSave = () => {
    if (!selectedCategoryId) return;
    const trekIds = orderedLinks
      .map((row) => Number(row.trekId || row.trek?.id))
      .filter(Boolean);
    saveOrderMutation.mutate({
      categoryId: selectedCategoryId,
      payload: {
        subcategoryId: scopeSubcategoryId || null,
        trekIds
      }
    });
  };

  const currentScopeLabel =
    scopeOptions.find((opt) => String(opt.value) === String(scopeSubcategoryId))?.label || 'Main category';

  return (
    <div className="module-page">
      <AdminBackLink to="/home-categories" label="Back to home categories" />
      <PageHeader
        title="Home Category Order"
        subtitle="Drag and drop treks to control the order inside a Home category or subcategory."
      />

      <FilterBar contentSx={{ py: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220, flex: '1 1 220px' }}>
          <InputLabel id="home-order-category">Category</InputLabel>
          <Select
            labelId="home-order-category"
            label="Category"
            value={selectedCategoryId}
            onChange={(e) => {
              setSelectedCategoryId(e.target.value);
              setScopeSubcategoryId('');
            }}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={String(cat.id)}>
                {cat.name} ({cat.sectionType})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220, flex: '1 1 220px' }}>
          <InputLabel id="home-order-scope">Scope</InputLabel>
          <Select
            labelId="home-order-scope"
            label="Scope"
            value={scopeSubcategoryId}
            onChange={(e) => setScopeSubcategoryId(e.target.value)}
          >
            {scopeOptions.map((opt) => (
              <MenuItem key={opt.value || 'root'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          disabled={!selectedCategoryId || saveOrderMutation.isLoading || orderedLinks.length === 0}
          onClick={handleSave}
          sx={{ alignSelf: 'center' }}
        >
          Save Order
        </Button>
      </FilterBar>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Trek Order for {selectedCategory ? selectedCategory.name : '—'} · {currentScopeLabel}
          </Typography>
          {linksQuery.isLoading ? (
            <div className="ops-loading-box">
              <CircularProgress size={28} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading treks…</Typography>
            </div>
          ) : !orderedLinks.length ? (
            <EmptyState
              title="No treks mapped yet"
              description="Use Home Categories → Trek Mapping to assign treks first."
            />
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {orderedLinks.map((row, index) => (
                <Box
                  component="li"
                  key={row.id}
                  draggable
                  onDragStart={() => onDragStart(row.id)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(row.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    p: '0.75rem 1rem',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: String(draggingId) === String(row.id) ? '#f1f5f9' : '#ffffff',
                    cursor: 'grab'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 22, textAlign: 'right' }}>
                      {index + 1}.
                    </Typography>
                    <Box
                      aria-hidden
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: 1,
                        border: '1px dashed #cbd5e1',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: '#94a3b8'
                      }}
                    >
                      ::
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {row.trek?.name || 'Untitled Trek'}
                      </Typography>
                      {row.trek?.category?.name && (
                        <Typography variant="caption" color="text.secondary">
                          {row.trek.category.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    ID: {row.trekId || row.trek?.id}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HomeCategoryOrder;
